import { useEffect, useState } from 'react';
import { HostSection } from '../../components/host/HostLayout';
import { useToast } from '../../components/ToastContext';
import { hostSupportTickets, hostSupportCreate, hostSupportTicket, hostSupportReply } from '../../api/host';

const categories = ['PAYOUTS', 'BOOKINGS', 'CALENDAR', 'KYC', 'TECHNICAL', 'OTHER'];
const priorities = ['LOW', 'MEDIUM', 'HIGH'];

const badgeTone = (status) => {
  const val = (status || '').toUpperCase();
  if (val === 'RESOLVED' || val === 'CLOSED') return 'bg-emerald-50 text-emerald-700 border-emerald-100';
  if (val === 'IN_PROGRESS') return 'bg-amber-50 text-amber-700 border-amber-100';
  return 'bg-slate-100 text-slate-700 border-slate-200';
};

const SupportPage = ({ accessToken }) => {
  const toast = useToast();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ subject: '', category: 'OTHER', priority: 'LOW', message: '', attachment: null });
  const [reply, setReply] = useState('');
  const [replyFile, setReplyFile] = useState(null);
  const [sending, setSending] = useState(false);

  const apiBase = process.env.REACT_APP_API_BASE_URL || '';
  const toAbs = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${apiBase}${url.startsWith('/') ? url : `/${url}`}`;
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      const res = await hostSupportTickets(accessToken);
      setTickets(res || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadTicket = async (id) => {
    try {
      const res = await hostSupportTicket(accessToken, id);
      setSelected(res.ticket);
      setMessages((res.messages || []).map((m) => ({ ...m, attachment_url: toAbs(m.attachment_url) })));
    } catch (err) {
      toast.error(err.message || 'Failed to load ticket');
    }
  };

  useEffect(() => {
    loadTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const submitTicket = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await hostSupportCreate(accessToken, createForm);
      toast.success('Ticket created');
      setCreateOpen(false);
      setCreateForm({ subject: '', category: 'OTHER', priority: 'LOW', message: '', attachment: null });
      await loadTickets();
      setSelected(res.ticket);
      setMessages((res.messages || []).map((m) => ({ ...m, attachment_url: toAbs(m.attachment_url) })));
    } catch (err) {
      toast.error(err.message || 'Failed to create ticket');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!selected) return;
    if (!reply && !replyFile) {
      toast.error('Message is required');
      return;
    }
    setSending(true);
    try {
      const msg = await hostSupportReply(accessToken, selected.id, { message: reply, attachment: replyFile });
      setReply('');
      setReplyFile(null);
      setMessages((prev) => [...prev, { ...msg, attachment_url: toAbs(msg.attachment_url) }]);
      await loadTickets();
    } catch (err) {
      toast.error(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const selectedStatus = selected?.status?.toUpperCase() || 'OPEN';
  const inputDisabled = selectedStatus === 'CLOSED';

  return (
    <HostSection
      title="Support"
      description="Get help from the Homevilas team."
      action={
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700"
        >
          Create New Ticket
        </button>
      }
    >
      <div className="p-6 space-y-6">
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
            <div>
              <p className="text-sm font-semibold text-slate-900">My Support Tickets</p>
              <p className="text-xs text-slate-500">All support requests youâ€™ve created.</p>
            </div>
            <span className="text-xs text-slate-500">{tickets.length} tickets</span>
          </div>
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">Ticket #</th>
                <th className="px-4 py-3">Subject</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Last Updated</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-3 text-slate-500">
                    Loading...
                  </td>
                </tr>
              )}
              {!loading && tickets.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-4 text-slate-500">
                    No tickets yet.
                  </td>
                </tr>
              )}
              {tickets.map((t) => (
                <tr key={t.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-900">{t.ticket_number || `#${t.id}`}</td>
                  <td className="px-4 py-3 text-slate-800">{t.subject}</td>
                  <td className="px-4 py-3 text-xs uppercase text-slate-600">{t.category}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${badgeTone(t.status)}`}>{t.status}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-600">
                    {t.last_message_at ? new Date(t.last_message_at).toLocaleString() : new Date(t.updated_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      type="button"
                      onClick={() => loadTicket(t.id)}
                      className="rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selected && (
          <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {selected.ticket_number || `Ticket #${selected.id}`} &middot; {selected.subject}
                </p>
                <p className="text-xs text-slate-500">
                  {selected.category} &bull; Priority: {selected.priority} &bull; Status: {selected.status}
                </p>
              </div>
              <span className={`rounded-full border px-2 py-1 text-[11px] font-semibold ${badgeTone(selected.status)}`}>
                {selected.status}
              </span>
            </div>
            <div className="max-h-[420px] space-y-3 overflow-y-auto px-4 py-4">
              {messages.length === 0 && <div className="text-sm text-slate-500">No messages yet.</div>}
              {messages.map((m) => (
                <div key={m.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  <div className="flex items-center justify-between text-xs text-slate-500">
                    <span className="font-semibold text-slate-700">{m.sender_type === 'ADMIN' ? 'Homevilas Support' : 'You'}</span>
                    <span>{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-800 whitespace-pre-wrap">{m.message}</p>
                  {m.attachment_url && (
                    <a
                      href={m.attachment_url}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-xs font-semibold text-emerald-700 hover:underline"
                    >
                      View attachment
                    </a>
                  )}
                </div>
              ))}
            </div>
            <div className="border-t border-slate-100 px-4 py-4">
              <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center">
                <textarea
                  disabled={inputDisabled}
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  placeholder={inputDisabled ? 'Ticket is closed' : 'Type your message'}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none disabled:bg-slate-50"
                />
                <div className="flex items-center gap-2">
                  <input
                    type="file"
                    disabled={inputDisabled}
                    accept="image/png,image/jpeg,application/pdf"
                    onChange={(e) => setReplyFile(e.target.files?.[0] || null)}
                    className="text-xs text-slate-600"
                  />
                  <button
                    type="button"
                    onClick={sendReply}
                    disabled={inputDisabled || sending}
                    className="rounded-lg bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-50"
                  >
                    {sending ? 'Sending...' : 'Send'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {createOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-lg font-semibold text-slate-900">Create Ticket</p>
                <p className="text-xs text-slate-500">Describe your issue and weâ€™ll get back to you.</p>
              </div>
              <button
                type="button"
                onClick={() => setCreateOpen(false)}
                className="rounded-full p-2 text-slate-500 hover:bg-slate-100"
              >
                âœ•
              </button>
            </div>
            <form className="mt-4 space-y-3" onSubmit={submitTicket}>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Subject</label>
                <input
                  required
                  value={createForm.subject}
                  onChange={(e) => setCreateForm((f) => ({ ...f, subject: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  placeholder="Short summary"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Category</label>
                  <select
                    value={createForm.category}
                    onChange={(e) => setCreateForm((f) => ({ ...f, category: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  >
                    {categories.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-semibold text-slate-600">Priority</label>
                  <select
                    value={createForm.priority}
                    onChange={(e) => setCreateForm((f) => ({ ...f, priority: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  >
                    {priorities.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Message</label>
                <textarea
                  required
                  value={createForm.message}
                  onChange={(e) => setCreateForm((f) => ({ ...f, message: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                  rows={3}
                  placeholder="Describe your issue"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-slate-600">Attachment (optional)</label>
                <input
                  type="file"
                  accept="image/png,image/jpeg,application/pdf"
                  onChange={(e) => setCreateForm((f) => ({ ...f, attachment: e.target.files?.[0] || null }))}
                  className="text-sm text-slate-600"
                />
              </div>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sending}
                  className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
                >
                  {sending ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </HostSection>
  );
};

export default SupportPage;

